import React, { useState } from "react";
import { Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Field, Input, Textarea, Select } from "@/components/Field";
import { CustomerSelect } from "@/components/admin/Editors";
import { useAdminData, TableSkeletonRows } from "@/components/admin/useAdminData";

const STATUSES = ["planning", "waiting_customer", "design", "development", "testing", "customer_review", "revision", "ready_launch", "completed", "on_hold", "cancelled"];
const PRIOS = ["low", "normal", "high", "urgent"];

export default function Projects() {
  const { t, sl } = useI18n();
  const { items: projects, loading, reload: load } = useAdminData("/projects");
  const [editing, setEditing] = useState(null);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/projects/${form.id}`, form);
      else await api.post("/projects", form);
      toast.success("OK"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Projekt „${p.name}" wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    try { await api.delete(`/projects/${p.id}`); toast.success("Projekt gelöscht"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-black">{t("admin.projects")}</h1>
        <button onClick={() => setEditing({ status: "planning", progress: 0, priority: "normal" })} className="btn-primary !py-2 !px-4 text-sm" data-testid="project-new"><Plus size={16} /> {t("common.create")}</button>
      </div>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-muted-foreground"><tr><th className="p-4">{t("common.name")}</th><th className="p-4">Kunde</th><th className="p-4">{t("common.status")}</th><th className="p-4">%</th><th className="p-4 text-right">{t("common.actions")}</th></tr></thead>
          <tbody>
            {loading ? <TableSkeletonRows cols={5} /> : projects.map((p) => (
              <tr key={p.id} className="border-t border-border" data-testid={`aproject-${p.id}`}>
                <td className="p-4 font-medium">{p.name}</td><td className="p-4">{p.customer_name}</td>
                <td className="p-4"><span className="rounded-full bg-primary/15 text-primary px-2.5 py-0.5 text-xs font-semibold capitalize">{sl(p.status)}</span></td>
                <td className="p-4">{p.progress}%</td>
                <td className="p-4 text-right"><div className="flex gap-1 justify-end"><button onClick={() => setEditing(p)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`project-edit-${p.id}`}>{t("common.edit")}</button><button onClick={() => del(p)} className="btn-ghost !py-1.5 !px-2.5 text-xs text-red-500 hover:text-red-600" data-testid={`project-delete-${p.id}`}><Trash2 size={13} /></button></div></td>
              </tr>
            ))}
            {!loading && projects.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{t("common.none")}</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <Drawer project={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function Drawer({ project, onClose, onSave }) {
  const { t, sl } = useI18n();
  const [f, setF] = useState({
    id: project.id, name: project.name || "", customer_id: project.customer_id || "", customer_name: project.customer_name || "",
    status: project.status || "planning", progress: project.progress ?? 0, priority: project.priority || "normal",
    description: project.description || "", internal_notes: project.internal_notes || "", deadline: (project.deadline || "").slice(0, 10),
  });
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="relative w-full max-w-lg bg-background border-l border-border h-full overflow-y-auto p-6 space-y-4" data-testid="project-drawer">
        <div className="flex items-center justify-between"><h2 className="font-display text-xl font-black">{t("admin.projects")}</h2><button type="button" onClick={onClose}><X size={20} /></button></div>
        <Field label={t("common.name")}><Input testid="proj-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Kunde"><CustomerSelect testid="proj-customer" value={f.customer_id} onChange={(id, name) => setF({ ...f, customer_id: id, customer_name: name })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("common.status")}><Select testid="proj-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>{STATUSES.map((s) => <option key={s} value={s}>{sl(s)}</option>)}</Select></Field>
          <Field label="Priorität"><Select testid="proj-prio" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>{PRIOS.map((s) => <option key={s} value={s}>{sl(s)}</option>)}</Select></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fortschritt %"><Input testid="proj-progress" type="number" min={0} max={100} value={f.progress} onChange={(e) => setF({ ...f, progress: Number(e.target.value) })} /></Field>
          <Field label="Deadline"><Input testid="proj-deadline" type="date" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} /></Field>
        </div>
        <Field label="Beschreibung (Kunde sichtbar)"><Textarea testid="proj-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <Field label="Interne Notizen (privat)"><Textarea testid="proj-notes" value={f.internal_notes} onChange={(e) => setF({ ...f, internal_notes: e.target.value })} /></Field>
        <button className="btn-primary w-full" data-testid="project-save">{t("common.save")}</button>
      </form>
    </div>
  );
}
